import type { ComparisonEntry } from './GearTestOrchestrator';
import type { GearSpecs, EquipmentBag } from '../equipment/types';
import { buildFittingSummary } from './FittingSummary';

export interface FittingRecommendation {
  headline: string;
  setupLabel: string;
  specBreakdown: string[];
  gearBreakdown: string[];
  distanceGain: number;
  accuracyGain: number;
  spinImprovement: string;
  confidenceNote: string;
  playerInsight: string;
}

const value = (entry: ComparisonEntry, key: string) => Number.parseFloat(entry.formatted[key]) || 0;
const signed = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}`;

/** Describe whole-configuration estimates, not unmeasured causal claims about
 * individual shafts, loft settings, or guaranteed real-world fitting gains. */
export function generateFittingRecommendation(
  handicap: number,
  clubKey: string,
  baseline: ComparisonEntry,
  bestSpec: ComparisonEntry,
  bestGear: ComparisonEntry,
  specs: GearSpecs,
  _gearBag: EquipmentBag | null,
  bestBall?: ComparisonEntry,
): FittingRecommendation {
  const summary = buildFittingSummary({ baseline, bestSpec, bestGear, bestBall });
  const final = summary.final;
  const accuracyGain = Math.abs(value(baseline, 'offline')) - Math.abs(value(final, 'offline'));
  const spinDelta = final.spinRate - baseline.spinRate;
  return {
    headline: `${signed(summary.totalGain)} yards total · simulated change`,
    setupLabel: summary.setupLabel,
    specBreakdown: [
      `${specs.loft}° loft · ${specs.shaftFlex} flex · ${specs.shaftWeight}g shaft`,
      `${signed(summary.specGain)} yards total versus your baseline with this complete spec configuration.`,
    ],
    gearBreakdown: [
      `${bestGear.label}: ${signed(summary.gearGain)} yards total versus the selected specs.`,
      ...(bestBall ? [`${bestBall.label}: ${signed(summary.ballGain)} yards total versus the selected equipment.`] : []),
    ],
    distanceGain: summary.totalGain,
    accuracyGain,
    spinImprovement: `${Math.round(final.spinRate).toLocaleString()} rpm simulated launch spin (${spinDelta >= 0 ? '+' : ''}${Math.round(spinDelta).toLocaleString()} vs baseline)`,
    confidenceNote: 'Simulated estimates, not guaranteed gains. Flight-model validation does not validate equipment rankings or real-turf roll. Confirm the selected setup with a launch-monitor fitting.',
    playerInsight: `Compared for your ${clubKey} and ${handicap} handicap profile. Distance, lateral finish and spin can trade off; review all three before choosing.`,
  };
}
